<template>
  <div class="cards-printed-view">
    <!-- Header -->
    <Header title="Cartes de Service Imprimées" />
    
    <div class="cards-content">
      <!-- Stats Cards -->
      <div class="stats-section">
        <div class="stat-card total">
          <div class="stat-icon">
            <span class="material-icons">badge</span>
          </div>
          <div class="stat-content">
            <h3>{{ totalCards }}</h3>
            <p>Total des cartes</p>
          </div>
        </div>
        
        <div class="stat-card printed">
          <div class="stat-icon">
            <span class="material-icons">print</span>
          </div>
          <div class="stat-content">
            <h3>{{ printedCards.length }}</h3>
            <p>Cartes capturées</p>
          </div>
        </div>
        
        <div class="stat-card unprinted">
          <div class="stat-icon">
            <span class="material-icons">pending</span>
          </div>
          <div class="stat-content">
            <h3>{{ unprintedCards.length }}</h3>
            <p>Cartes non capturées</p>
          </div>
        </div>
      </div>

      <!-- Filters and Actions -->
      <div class="control-panel">
        <div class="panel-header">
          <h3>Liste des cartes</h3>
          <div class="header-actions">
            <button class="refresh-btn" @click="loadCards" title="Rafraîchir">
              <span class="material-icons">refresh</span>
              Rafraîchir
            </button>
            <button class="export-btn" @click="exportData" title="Exporter les données">
              <span class="material-icons">download</span>
              Exporter
            </button>
          </div>
        </div>
        
        <div class="filters">
          <div class="filter-group">
            <label>Filtre :</label>
            <select v-model="filterType" @change="applyFilter">
              <option value="all">Toutes les cartes</option>
              <option value="printed">Cartes capturées</option>
              <option value="unprinted">Cartes non capturées</option>
            </select>
          </div>
          
          <div class="search-group">
            <span class="material-icons">search</span>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Rechercher par nom, matricule ou numéro de carte..."
              @input="applyFilter"
            />
          </div>
        </div>
      </div>

      <!-- Cards Table -->
      <div class="cards-table-container">
        <div v-if="loading" class="loading-container">
          <div class="spinner"></div>
          <p>Chargement des cartes...</p>
        </div>
        
        <div v-else-if="filteredCards.length === 0" class="empty-state">
          <span class="material-icons">inbox</span>
          <p>Aucune carte trouvée</p>
        </div>
        
        <table v-else class="cards-table">
          <thead>
            <tr>
              <th>N° Carte</th>
              <th>Matricule</th>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Date de capture</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="card in filteredCards" :key="card.agentId">
              <td class="card-number">
                <span class="badge">{{ card.cardNumber.toString().padStart(3, '0') }}</span>
              </td>
              <td>{{ card.matricule || 'N/A' }}</td>
              <td>{{ card.nom || 'N/A' }}</td>
              <td>{{ card.prenom || 'N/A' }}</td>
              <td>{{ card.capturedDate ? formatDate(card.capturedDate) : 'Non capturée' }}</td>
              <td>
                <span class="status-badge printed">
                  <span class="material-icons">check_circle</span>
                  Capturée
                </span>
              </td>
              <td class="actions">
                <button 
                  class="view-btn" 
                  @click="viewAgent(card.agentId)"
                  title="Voir le profil de l'agent"
                >
                  <span class="material-icons">visibility</span>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Header from '@/components/Header.vue'
import { 
  getAllAgentsWithCards, 
  getPrintedCards, 
  getUnprintedCards,
  exportCardNumberData,
  loadCardsFromCentralFile
} from '@/services/cardNumberService'

const router = useRouter()

// State
const loading = ref(false)
const allCards = ref([])
const filterType = ref('all')
const searchQuery = ref('')

// Computed
const printedCards = computed(() => {
  return allCards.value.filter(card => card.printed === true)
})

const unprintedCards = computed(() => {
  return allCards.value.filter(card => card.printed !== true)
})

const totalCards = computed(() => {
  return allCards.value.length
})

const filteredCards = computed(() => {
  let filtered = [...allCards.value]
  
  // Appliquer le filtre de type
  if (filterType.value === 'printed') {
    filtered = filtered.filter(card => card.capturedDate)
  } else if (filterType.value === 'unprinted') {
    filtered = filtered.filter(card => !card.capturedDate)
  }
  
  // Appliquer la recherche
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim()
    filtered = filtered.filter(card => {
      const cardNumber = card.cardNumber.toString()
      const matricule = (card.matricule || '').toLowerCase()
      const nom = (card.nom || '').toLowerCase()
      const prenom = (card.prenom || '').toLowerCase()
      
      return cardNumber.includes(query) ||
             matricule.includes(query) ||
             nom.includes(query) ||
             prenom.includes(query)
    })
  }
  
  // Trier par numéro de carte (décroissant pour voir les plus récents en premier)
  return filtered.sort((a, b) => b.cardNumber - a.cardNumber)
})

// Methods
const loadCards = async () => {
  loading.value = true
  try {
    // Essayer de charger depuis le fichier centralisé d'abord
    const fileData = await loadCardsFromCentralFile()
    if (fileData && fileData.agents && fileData.agents.length > 0) {
      allCards.value = fileData.agents
      console.log(`${allCards.value.length} cartes chargées depuis le fichier centralisé`)
    } else {
      // Sinon, charger depuis localStorage
      allCards.value = getAllAgentsWithCards()
      console.log(`${allCards.value.length} cartes chargées depuis localStorage`)
    }
  } catch (error) {
    console.error('Erreur lors du chargement des cartes:', error)
    // En cas d'erreur, charger depuis localStorage
    allCards.value = getAllAgentsWithCards()
  } finally {
    loading.value = false
  }
}

const applyFilter = () => {
  // Le computed filteredCards se met à jour automatiquement
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    return 'Date invalide'
  }
}

const viewAgent = (agentId) => {
  if (agentId) {
    router.push(`/agent/${agentId}`)
  }
}

const exportData = () => {
  try {
    const data = exportCardNumberData()
    const dataStr = JSON.stringify(data, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'cartes_service.json'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    console.log('✅ Données exportées avec succès')
    console.log('💡 Remplacez le fichier public/data/cartes_service.json par le fichier téléchargé pour synchroniser')
  } catch (error) {
    console.error('Erreur lors de l\'export:', error)
    alert('Erreur lors de l\'export des données')
  }
}

// Lifecycle
onMounted(() => {
  loadCards()
})
</script>

<style scoped>
@import '@/assets/css/variables.css';

.cards-printed-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.cards-content {
  flex: 1;
  padding: 20px;
  background-color: #f5f7fa;
  overflow-y: auto;
}

/* Stats Section */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
}

.stat-card.total .stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-card.printed .stat-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card.unprinted .stat-icon {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
}

.stat-content h3 {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #333;
}

.stat-content p {
  margin: 5px 0 0 0;
  font-size: 14px;
  color: #666;
}

/* Control Panel */
.control-panel {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.refresh-btn, .export-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background-color: var(--turquoise-primary);
  color: white;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.refresh-btn:hover, .export-btn:hover {
  background-color: var(--turquoise-dark);
}

.filters {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-group label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.filter-group select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.search-group {
  flex: 1;
  min-width: 300px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.search-group .material-icons {
  color: #999;
  font-size: 20px;
}

.search-group input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
}

/* Table */
.cards-table-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.loading-container, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state .material-icons {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(25, 118, 210, 0.1);
  border-radius: 50%;
  border-top-color: #1976d2;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.cards-table {
  width: 100%;
  border-collapse: collapse;
}

.cards-table thead {
  background-color: #f5f7fa;
}

.cards-table th {
  padding: 12px 15px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
}

.cards-table td {
  padding: 12px 15px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  color: #666;
}

.cards-table tbody tr:hover {
  background-color: #f9f9f9;
}

.cards-table tbody tr.printed {
  background-color: #f0f9ff;
}

.card-number .badge {
  display: inline-block;
  padding: 4px 10px;
  background-color: var(--turquoise-primary);
  color: white;
  border-radius: 12px;
  font-weight: 600;
  font-size: 13px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.printed {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.status-badge.pending {
  background-color: #fff3e0;
  color: #e65100;
}

.status-badge .material-icons {
  font-size: 16px;
}

.actions {
  display: flex;
  gap: 5px;
}

.view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background-color: #e3f2fd;
  color: #1976d2;
  cursor: pointer;
  transition: all 0.2s;
}

.view-btn:hover {
  background-color: #1976d2;
  color: white;
}

.view-btn .material-icons {
  font-size: 18px;
}

@media (max-width: 768px) {
  .cards-table {
    font-size: 12px;
  }
  
  .cards-table th,
  .cards-table td {
    padding: 8px;
  }
  
  .filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-group {
    min-width: 100%;
  }
}
</style>

